import {
  Building2,
  GanttChartSquare,
  Gauge,
  CalendarClock,
  LifeBuoy,
  ListOrdered,
  LogOut,
  Settings,
  SquareStack,
  UserRound,
} from 'lucide-react'
import { NavLink, Outlet } from 'react-router-dom'

import { useAuth } from '@/features/auth/auth-context'
import { isAdmin } from '@/features/auth/permissions'
import { useSession } from '@/features/auth/use-session'
import {
  DensityToggle,
  DevUserSwitcher,
  LocalePicker,
  ThemeToggle,
  UserAvatar,
} from '@/shared/components/common'
import { CommandPalette } from '@/shared/components/common/CommandPalette'
import { cn } from '@/shared/lib/cn'
import { useT } from '@/shared/i18n'
import { QuickBar } from '@/features/quick-add/QuickBar'

interface NavItem {
  to: string
  /** La CLAVE del catálogo, no el texto: la navegación se dibuja en el idioma de quien entra. */
  labelKey: string
  icon: typeof Building2
  adminOnly?: boolean
}

const NAV: NavItem[] = [
  { to: '/portfolio', labelKey: 'nav.portfolio', icon: SquareStack },
  { to: '/priorities', labelKey: 'nav.priorities', icon: ListOrdered },
  { to: '/timeline', labelKey: 'nav.timeline', icon: GanttChartSquare },
  // Sólo admin: el tablero mide al equipo persona por persona.
  { to: '/metrics', labelKey: 'nav.metrics', icon: Gauge, adminOnly: true },
  { to: '/companies', labelKey: 'nav.companies', icon: Building2 },
  { to: '/due-soon', labelKey: 'nav.dueSoon', icon: CalendarClock },
  { to: '/me/tasks', labelKey: 'nav.myTasks', icon: UserRound },
  { to: '/support', labelKey: 'nav.support', icon: LifeBuoy },
]

function navClasses({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 text-xs',
    'transition-colors duration-[var(--motion-fast)]',
    isActive ? 'bg-surface-2 text-text-1' : 'text-text-2 hover:bg-surface-2 hover:text-text-1',
  )
}

/**
 * Shell interno: navegacion densa a la izquierda, contenido a la derecha.
 *
 * El conmutador `⌘K` se monta ACA y no en `AppProviders`: asi el portal, que
 * usa otro layout, no lo tiene ni puede tenerlo. Un cliente no abre un buscador
 * que lista empresas.
 */
export function InternalLayout() {
  const t = useT()
  const { me } = useSession()
  const { signOut } = useAuth()

  return (
    <div className="flex min-h-dvh bg-bg text-text-1">
      {/*
        Fija a la altura de la pantalla. Sin `sticky`, la barra medía lo que la
        página: con un portafolio de cien filas, el pie —tema, idioma, salir—
        quedaba a 3.000 px de distancia, al final de todo.
      */}
      <aside className="hairline-r sticky top-0 hidden h-dvh w-[200px] shrink-0 flex-col gap-1 overflow-y-auto border-r border-hairline bg-surface-1 p-2 md:flex">
        <div className="flex items-center gap-2 px-2 py-2">
          <span aria-hidden="true" className="h-4 w-[var(--spine-w)] rounded-full bg-signal" />
          <span className="font-display text-base">{t('nav.appName')}</span>
        </div>

        <QuickBar className="mb-2 px-1" />

        <nav className="flex flex-col gap-0.5" aria-label={t('nav.mainLabel')}>
          {/*
            `adminOnly` estaba declarado en el tipo y no lo miraba nadie: el
            filtro faltaba, así que marcar un ítem no hacía nada. Un link que
            lleva a un 403 es peor que no tenerlo — promete una pantalla que no
            existe para quien lo ve.
          */}
          {NAV.filter((item) => !item.adminOnly || isAdmin(me)).map(
            ({ to, labelKey, icon: Icon }) => (
              <NavLink key={to} to={to} className={navClasses}>
                <Icon size={14} aria-hidden="true" />
                {t(labelKey)}
              </NavLink>
            ),
          )}

          {isAdmin(me) ? (
            <NavLink to="/settings/fases" className={navClasses}>
              <Settings size={14} aria-hidden="true" />
              {t('nav.settings')}
            </NavLink>
          ) : null}
        </nav>

        <p className="mt-auto px-2 pb-2 text-2xs text-text-3">
          <kbd className="num">⌘K</kbd> {t('nav.commandHint')}
        </p>

        <div className="flex flex-col gap-2 px-1 pb-1">
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <DensityToggle />
          </div>
          <LocalePicker />
          <DevUserSwitcher />
          <div className="hairline-t flex items-center justify-between gap-2 pt-2">
            <UserAvatar user={me} withName className="min-w-0" />
            <button
              type="button"
              onClick={() => void signOut()}
              aria-label={t('nav.signOut')}
              title={t('nav.signOut')}
              className="shrink-0 rounded-[var(--radius-control)] p-1 text-text-3 hover:text-text-1"
            >
              <LogOut size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        <Outlet />
      </main>

      <CommandPalette />
    </div>
  )
}
